/**
 * /auth/reset — Demande de réinitialisation du mot de passe.
 *
 * Refonte 9.P.1 — alignée avec LoginPage 9.O.1 :
 *   - Inter exclusif, KollectiviaWordmark canonique.
 *   - Tokens slate (zéro stone-*), brand-600 CTA.
 *   - Validation email onBlur (parité LoginPage), spinner submit inline.
 *   - min-h-dvh + safe-area-inset, focus-visible:ring-brand-500.
 *
 * Sécurité :
 *   - Aucun `console.log` du payload.
 *   - Le backend retourne 200 OK uniformément (anti-énumération) — l'UI
 *     affiche le même message « lien envoyé si compte existe » dans les 2 cas.
 */

import { type FormEvent, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { KollectiviaWordmark } from "../components/brand/KollectiviaMark";
import { ApiException, api } from "../lib/api";
import { getApiErrorMessage, normalizeLang } from "../lib/apiErrorMessage";

// eslint-disable-next-line no-useless-escape
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ResetRequestPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const emailId = useId();
  const errorId = useId();

  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);

  const emailValid = email === "" || EMAIL_RE.test(email);
  const emailShowError = emailTouched && !emailValid;
  const canSubmit = email !== "" && emailValid && !submitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!emailValid) {
      emailRef.current?.focus();
      setEmailTouched(true);
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/v1/auth/reset/request", { email });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(getApiErrorMessage(err, lang) || t("errors.generic"));
      } else {
        setError(t("errors.generic"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 pb-[env(safe-area-inset-bottom)]">
      <main
        id="main"
        role="main"
        tabIndex={-1}
        className="flex w-full flex-1 flex-col items-center justify-center px-6 py-12 focus:outline-none"
      >
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center justify-between">
            <KollectiviaWordmark markSize={24} textSize={16} />
            <LanguageSwitcher />
          </div>

          <h1 className="text-pretty text-2xl font-semibold tracking-tight text-slate-900">
            {t("auth.reset.request_title")}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {t("auth.reset.request_subtitle")}
          </p>

          {done ? (
            <div
              role="status"
              aria-live="polite"
              className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800"
            >
              {t("auth.reset.request_sent")}
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="mt-6 space-y-5">
              <div className="space-y-1">
                <label
                  htmlFor={emailId}
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("auth.login.email")}
                </label>
                <input
                  ref={emailRef}
                  id={emailId}
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder={t("auth.login.email_placeholder")}
                  disabled={submitting}
                  aria-invalid={emailShowError ? "true" : undefined}
                  aria-describedby={error ? errorId : undefined}
                  className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 aria-[invalid=true]:border-red-400 aria-[invalid=true]:focus:ring-red-400 motion-reduce:transition-none"
                />
                {emailShowError && (
                  <p role="alert" className="text-xs font-medium text-red-700">
                    {t("auth.login.email_invalid")}
                  </p>
                )}
              </div>

              {error && (
                <p
                  id={errorId}
                  role="alert"
                  aria-live="polite"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex w-full touch-manipulation items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 active:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none motion-reduce:transition-none"
              >
                {submitting && (
                  <span
                    aria-hidden="true"
                    className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none"
                  />
                )}
                {submitting
                  ? t("auth.reset.request_submitting")
                  : t("auth.reset.request_submit")}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <Link
              to="/auth/login"
              className="rounded text-brand-600 underline-offset-2 hover:text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            >
              ← {t("common.back")}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
